/**
 * useLiquidGlass — attach Apple Liquid Glass edge refraction to a small DOM surface.
 *
 * Liquid Glass's signature over plain glassmorphism is REFRACTION: content behind the
 * element's edges is bent (lensed) like real curved glass, while the interior stays
 * legible. This hook applies that effect to a caller-provided node via an SVG
 * `feDisplacementMap` referenced from `backdrop-filter: url(#…)`, generating a
 * displacement map sized to the element (regenerated on resize).
 *
 * Scope: SMALL, rounded-rect surfaces — popovers, the search pill, cards. It is NOT
 * for the app-shell bezel (that L is full-viewport + fold-animated and uses its own
 * static filter in InstrumentBezel.tsx). Refraction is DELIGHT, not function: it is
 * Chromium-only (SVG-in-backdrop-filter), and it yields entirely to the accessibility
 * floor and to Calm mode — in every fallback the element keeps its CSS glass tokens,
 * so nothing that carries meaning ever depends on the refraction rendering.
 *
 * Conventions mirror the existing hooks: a returned ref (see useVantaClouds), a
 * module-level mount ref-count for the one shared <svg> defs root (see
 * useEnvironmentalCanvas), reactive `matchMedia` gating, and thorough teardown.
 * DOM is only ever touched inside the effect.
 */

import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

export interface LiquidGlassOptions {
  /** Displacement strength; negative magnifies (bulges), positive pinches. */
  scale?: number;
  /** Per-channel offset for chromatic aberration at the edge; 0 disables it. */
  chroma?: number;
  /** Neutral interior inset as a fraction of the smaller dimension (0–0.5). */
  border?: number;
  /** Displacement-map curvature smoothness (px): small = hard rim, large = soft dome. */
  mapBlur?: number;
  /** Backdrop blur (px) applied alongside the refraction. */
  blur?: number;
  /**
   * Backdrop saturation MULTIPLIER — unitless, like the CSS `saturate()` function
   * (1.5 → `saturate(150%)`), NOT a percentage. Passing a percentage-style value
   * (e.g. 180) is a footgun — `saturate(180)` is 18000%; such values are coerced
   * (÷100) with a dev warning and clamped. Pass 1.8, not 180.
   */
  saturate?: number;
  /** Corner radius override (px); defaults to the element's computed border-radius. */
  radius?: number | null;
  /** Master switch — pass false to leave the element on its plain CSS glass. */
  enabled?: boolean;
}

export interface LiquidGlassHandle<T extends HTMLElement = HTMLDivElement> {
  /** Attach to the glass element. */
  ref: RefObject<T | null>;
  /** True when the browser can render SVG refraction in backdrop-filter (Chromium). */
  supported: boolean;
  /** True when refraction is actually applied (supported AND not gated off). */
  active: boolean;
}

const DEFAULTS: Required<Omit<LiquidGlassOptions, 'radius' | 'enabled'>> & {
  radius: number | null;
  enabled: boolean;
} = {
  scale: -112,
  chroma: 6,
  border: 0.07,
  mapBlur: 12,
  blur: 3,
  saturate: 1.5,
  radius: null,
  enabled: true,
};

/**
 * Normalise the `saturate` MULTIPLIER, defusing the percentage footgun: the value
 * is emitted straight into `saturate(${n})`, so a caller who passes a percentage
 * (e.g. 180, expecting `saturate(180%)`) would get `saturate(180)` = 18000%. Any
 * value ≥ 20 is almost certainly a mis-typed percentage — coerce it (÷100) and warn
 * in dev; then clamp to a sane [0, 10] range so no input can blow out the backdrop.
 */
function normalizeSaturate(saturate: number): number {
  let s = Number.isFinite(saturate) ? saturate : DEFAULTS.saturate;
  if (s >= 20) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[useLiquidGlass] saturate=${saturate} looks like a percentage; ` +
          `it is a multiplier (1.5 = 150%). Coercing to ${s / 100}. Pass ${s / 100}, not ${saturate}.`,
      );
    }
    s /= 100;
  }
  return Math.max(0, Math.min(10, s));
}

// ── One shared, hidden <svg> defs root for every consumer (ref-counted) ──────────
// A single <svg> holds every instance's <filter>; the first consumer creates it and
// the last one removes it, so route transitions never leave an orphaned node. This
// mirrors the mountCount pattern in useEnvironmentalCanvas.ts.
const DEFS_ROOT_ID = 'lg-defs-root';
let defsMountCount = 0;
let uidCounter = 0;

function acquireDefsRoot(): SVGSVGElement {
  let root = document.getElementById(DEFS_ROOT_ID) as SVGSVGElement | null;
  if (!root) {
    root = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    root.setAttribute('id', DEFS_ROOT_ID);
    root.setAttribute('width', '0');
    root.setAttribute('height', '0');
    root.setAttribute('aria-hidden', 'true');
    root.style.position = 'absolute';
    root.style.pointerEvents = 'none';
    document.body.appendChild(root);
  }
  defsMountCount += 1;
  return root;
}

function releaseDefsRoot(): void {
  defsMountCount -= 1;
  if (defsMountCount <= 0) {
    defsMountCount = 0;
    document.getElementById(DEFS_ROOT_ID)?.remove();
  }
}

/**
 * Feature-detect SVG-filter-in-backdrop-filter. This is effectively Chromium-only;
 * Safari/Firefox return false here and keep the CSS glass fallback. (Note: an
 * `@supports` query can false-positive on Safari, which parses url() but renders
 * nothing — this style probe is the more reliable signal.)
 *
 * The `window.CSS.supports` guard is the jsdom floor: jsdom has no CSSOM support
 * layer at all (`window.CSS` is undefined) yet its permissive style parser still
 * RETAINS `backdrop-filter: url(#…)`, so the bare style probe alone false-positives
 * under test and the hook would claim to refract in an environment that renders
 * nothing. Requiring a real `CSS.supports` implementation keeps the test/SSR path
 * on the honest CSS-glass fallback, matching Safari/Firefox behaviour.
 */
let urlBackdropSupport: boolean | null = null;
function supportsUrlBackdrop(): boolean {
  // The answer is a static browser capability — it never changes within a session —
  // so memoise it: the probe (build a detached node, set a style, read it back) then
  // runs at most once per environment instead of on every effect run / resize. We
  // memoise LAZILY on first call rather than at module scope so there is no import-
  // time DOM side-effect and the guard below is evaluated in the environment that
  // actually consumes the hook (an SSR pass returns false WITHOUT caching, so the
  // client bundle still runs the real probe).
  if (urlBackdropSupport !== null) return urlBackdropSupport;
  if (typeof document === 'undefined') return false;
  if (typeof window === 'undefined' || typeof window.CSS?.supports !== 'function') {
    return false;
  }
  const probe = document.createElement('div');
  probe.style.backdropFilter = 'url(#lg-probe)';
  urlBackdropSupport = probe.style.backdropFilter.includes('url');
  return urlBackdropSupport;
}

/** Build the displacement-map SVG as a data URI, sized to the element.
 *
 * The interior rect fill is FULLY OPAQUE neutral gray (`hsl(0 0% 50%)`, R=B=0.5 =
 * zero displacement). A partial alpha there would let the edge gradients composite
 * through and shift the R/B channels off 0.5, displacing the "undistorted" interior
 * by a few px — Liquid Glass bends the edges only. Keep alpha at 1. */
function buildDisplacementMapUri(
  width: number,
  height: number,
  radius: number,
  border: number,
  mapBlur: number,
): string {
  const inset = Math.min(width, height) * border;
  const innerW = Math.max(0, width - inset * 2);
  const innerH = Math.max(0, height - inset * 2);
  // Clamp the inner rect's corner radius to its own half-dimensions. On small
  // surfaces (or with a large `radius` option) an un-clamped rx exceeds innerW/2 or
  // innerH/2; the SVG renderer then silently rounds the corner inward, shrinking the
  // opaque neutral zone and letting the red/blue edge gradients bleed into the
  // interior — displacing the center, the exact failure the opaque-interior invariant
  // exists to prevent.
  const innerRadius = Math.max(0, Math.min(radius, innerW / 2, innerH / 2));
  const svg =
    `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">` +
    `<defs>` +
    `<linearGradient id="r" x1="100%" y1="0%" x2="0%" y2="0%">` +
    `<stop offset="0%" stop-color="#0000"/><stop offset="100%" stop-color="red"/></linearGradient>` +
    `<linearGradient id="b" x1="0%" y1="0%" x2="0%" y2="100%">` +
    `<stop offset="0%" stop-color="#0000"/><stop offset="100%" stop-color="blue"/></linearGradient>` +
    `</defs>` +
    `<rect width="${width}" height="${height}" fill="black"/>` +
    `<rect width="${width}" height="${height}" rx="${radius}" fill="url(#r)"/>` +
    `<rect width="${width}" height="${height}" rx="${radius}" fill="url(#b)" style="mix-blend-mode:difference"/>` +
    `<rect x="${inset}" y="${inset}" width="${innerW}" height="${innerH}" rx="${innerRadius}" ` +
    `fill="hsl(0 0% 50%)" style="filter:blur(${mapBlur}px)"/>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Build the <filter> markup with per-channel displacement for chromatic aberration.
 * `color-interpolation-filters="sRGB"` is MANDATORY — the default linearRGB re-maps
 * the map's neutral gray and smears displacement across the whole element.
 *
 * The filter region is padded to -20%..140% (vs the objectBoundingBox default of
 * 0..100%): edge refraction pushes backdrop pixels OUTWARD past the element box, and
 * a region clipped to the box crops that outward lensing into a hard-cut rim. The
 * static #lg-lens filter pads for the same reason. 20% comfortably clears the
 * default scale (-112) at popover sizes without inflating the filtered area.
 */
function buildFilterMarkup(
  filterId: string,
  mapUri: string,
  scale: number,
  chroma: number,
): string {
  const r = scale;
  const g = scale + chroma;
  const b = scale + chroma * 2;
  return (
    `<filter id="${filterId}" x="-20%" y="-20%" width="140%" height="140%" color-interpolation-filters="sRGB">` +
    `<feImage href="${mapUri}" result="map" preserveAspectRatio="none"/>` +
    `<feDisplacementMap in="SourceGraphic" in2="map" scale="${r}" xChannelSelector="R" yChannelSelector="B" result="red"/>` +
    `<feColorMatrix in="red" type="matrix" values="1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" result="redOnly"/>` +
    `<feDisplacementMap in="SourceGraphic" in2="map" scale="${g}" xChannelSelector="R" yChannelSelector="B" result="green"/>` +
    `<feColorMatrix in="green" type="matrix" values="0 0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 1 0" result="greenOnly"/>` +
    `<feDisplacementMap in="SourceGraphic" in2="map" scale="${b}" xChannelSelector="R" yChannelSelector="B" result="blue"/>` +
    `<feColorMatrix in="blue" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 0 0 1 0" result="blueOnly"/>` +
    `<feBlend in="redOnly" in2="greenOnly" mode="screen" result="rg"/>` +
    `<feBlend in="rg" in2="blueOnly" mode="screen" result="rgb"/>` +
    `<feGaussianBlur in="rgb" stdDeviation="0.3"/>` +
    `</filter>`
  );
}

/**
 * Applies Liquid Glass refraction to a caller-provided element. Returns a ref to
 * attach plus `supported`/`active` flags so the caller can keep its CSS glass tokens
 * as the fallback in every non-Chromium / accessibility / calm case.
 */
export function useLiquidGlass<T extends HTMLElement = HTMLDivElement>(
  options: LiquidGlassOptions = {},
): LiquidGlassHandle<T> {
  const ref = useRef<T | null>(null);
  const [supported, setSupported] = useState(false);
  const [active, setActive] = useState(false);

  // Snapshot options into primitives so the effect deps are stable (no object identity
  // churn re-running the whole setup every render).
  const cfg = { ...DEFAULTS, ...options };
  const { scale, chroma, border, mapBlur, blur, radius, enabled } = cfg;
  // Guard the percentage footgun (see normalizeSaturate): saturate is a multiplier.
  const saturate = normalizeSaturate(cfg.saturate);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const canRefract = supportsUrlBackdrop();
    setSupported(canRefract);

    if (!canRefract || !enabled) {
      setActive(false);
      return; // Element keeps whatever CSS glass it already has.
    }

    // Gating: refraction is decoration → it yields to accessibility + Calm mode.
    const queries = [
      window.matchMedia('(prefers-reduced-transparency: reduce)'),
      window.matchMedia('(prefers-contrast: more)'),
      window.matchMedia('(prefers-reduced-motion: reduce)'),
    ];
    const isCalm = () =>
      typeof document !== 'undefined' &&
      document.documentElement.hasAttribute('data-calm-mode');
    const isGatedOff = () => queries.some((q) => q.matches) || isCalm();

    const uid = `lg-${(uidCounter += 1)}`;
    const defsRoot = acquireDefsRoot();
    const filterHost = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    filterHost.setAttribute('data-lg-filter', uid);
    defsRoot.appendChild(filterHost);

    let rafId = 0;

    function clearBackdrop() {
      if (!el) return;
      el.style.backdropFilter = '';
      (el.style as CSSStyleDeclaration & { webkitBackdropFilter?: string }).webkitBackdropFilter = '';
    }

    function apply() {
      if (!el) return;
      if (isGatedOff()) {
        filterHost.innerHTML = '';
        clearBackdrop();
        setActive(false);
        return;
      }
      const rect = el.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      const resolvedRadius =
        radius ?? (parseFloat(getComputedStyle(el).borderRadius) || 20);
      const mapUri = buildDisplacementMapUri(w, h, resolvedRadius, border, mapBlur);
      filterHost.innerHTML = buildFilterMarkup(uid, mapUri, scale, chroma);
      const filterValue = `url(#${uid}) blur(${blur}px) saturate(${saturate})`;
      el.style.backdropFilter = filterValue;
      (el.style as CSSStyleDeclaration & { webkitBackdropFilter?: string }).webkitBackdropFilter = filterValue;
      setActive(true);
    }

    function scheduleApply() {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(apply);
    }

    apply();

    // Regenerate the map when the element resizes (the map is size-specific).
    const ro = new ResizeObserver(scheduleApply);
    ro.observe(el);

    // React to accessibility preference changes at runtime.
    const onChange = () => scheduleApply();
    queries.forEach((q) => q.addEventListener('change', onChange));

    // Calm mode is a runtime attribute on <html> (not a media query), so observe it
    // directly — otherwise the effect would only honor it on mount, contradicting the
    // "yields entirely to Calm mode" contract when it's toggled with the panel open.
    const calmObserver = new MutationObserver(scheduleApply);
    calmObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-calm-mode'],
    });

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      queries.forEach((q) => q.removeEventListener('change', onChange));
      calmObserver.disconnect();
      filterHost.remove();
      releaseDefsRoot();
      clearBackdrop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, chroma, border, mapBlur, blur, saturate, radius, enabled]);

  return { ref, supported, active };
}
